"use client";

import * as React from "react";

import { ChevronLeftIcon, Menu as IconMenu } from "lucide-react";

import { Button, type ButtonProps } from "@/shared-web.ui/ui.button";
import { Flex } from "@/shared-web.ui/ui.flex";
import { IconButton } from "@/shared-web.ui/ui.icon-button";
import { Separator } from "@/shared-web.ui/ui.separator";
import { cn } from "@/shared-web.ui/util.cn";

import { LogoCaf } from "../assets.logo";
import { AppContainer } from "../ui.app-container/ui.app-container";

interface HeaderCompProps {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
}
const HeaderComp = (props: HeaderCompProps) => {
  const { children, className, sticky = true } = props;
  return (
    <header
      className={cn(
        `
          inset-x-0 top-0 z-50 flex h-[4.5rem] items-center
          rounded-b-4 bg-accent-1 shadow-3
        `,
        sticky && "sticky",
        className,
      )}
    >
      {children}
    </header>
  );
};

type MobileNavbarTab = "category" | "main";

type MobileNavbarContextProps = {
  setter: {
    setTab: React.Dispatch<React.SetStateAction<MobileNavbarTab>>;
  };
  value: {
    tab: MobileNavbarTab;
  };
};

const MobileNavbarContext
  = React.createContext<MobileNavbarContextProps | null>(null);

const useMobileNavbar = () => {
  const context = React.useContext(MobileNavbarContext);
  if (!context) {
    throw new Error("useMobileNavbar must be used within a MobileNavbar.Root ");
  }
  return context;
};

interface MobileNavbarRootProps {
  children: React.ReactNode;
}
const MobileNavbarRoot = (props: MobileNavbarRootProps) => {
  const { children } = props;

  const [tab, setTab] = React.useState<MobileNavbarTab>("main");

  return (
    <MobileNavbarContext.Provider
      value={{
        setter: { setTab },
        value: { tab },
      }}
    >
      {children}
    </MobileNavbarContext.Provider>
  );
};

interface MobileNavbarHeaderProps {
  children: React.ReactNode;
}
const MobileNavbarHeader = (props: MobileNavbarHeaderProps) => {
  const { children } = props;
  return (
    <>
      <div
        className={`
          m-4 flex h-10 items-center justify-between p-2
        `}
      >
        {children}
      </div>
      <Separator className="mb-6 ml-6" size="1" />
    </>
  );
};

interface MobileNavbarContentProps {
  children: React.ReactNode;
  tab: MobileNavbarTab;
}
const MobileNavbarContent = (props: MobileNavbarContentProps) => {
  const { children, tab } = props;
  const {
    value: { tab: curTab },
  } = useMobileNavbar();

  if (tab === curTab) {
    return children;
  }
};

type MobileNavbarBodyProps = React.ComponentPropsWithoutRef<typeof Flex>;
const MobileNavbarBody = React.forwardRef<
  React.ElementRef<typeof Flex>,
  MobileNavbarBodyProps
>(({ className, ...props }, ref) => {
  return (
    <Flex
      className={cn("p-6 pt-0", className)}
      direction="column"
      gap="6"
      ref={ref}
      {...props}
    />
  );
});
MobileNavbarBody.displayName = "MobileNavbarBody";

interface MenuIconProps
  extends Pick<
    React.ComponentPropsWithoutRef<typeof IconButton>,
    "className" | "screenReaders"
  > {
  Icon?: React.ReactNode;
}
const MenuIcon = React.forwardRef<
  React.ElementRef<typeof IconButton>,
  MenuIconProps
>((props, ref) => {
  const { Icon, ...menuIconProps } = props;

  return (
    <IconButton
      {...menuIconProps}
      color="gray"
      radius="full"
      size="2"
      variant="ghost"
      ref={ref}
    >
      {Icon || <IconMenu />}
    </IconButton>
  );
});
MenuIcon.displayName = "MenuIcon";

interface BackToButtonProps extends ButtonProps {}
const BackToButton = (props: BackToButtonProps) => {
  const { children, ...buttonProps } = props;
  return (
    <Button
      size="3"
      variant="ghost"
      className="align-middle"
      {...buttonProps}
    >
      <ChevronLeftIcon className="size-5" />
      {children}
    </Button>
  );
};

interface MobileWithBackContainerProps {
  children: React.ReactNode;
}
const MobileWithBackContainer = (props: MobileWithBackContainerProps) => {
  const { children } = props;
  return (
    <AppContainer>
      <Flex
        align="center"
        direction="row"
        justify="between"
        className={`
          px-2
          sm:px-0
        `}
      >
        {children}
      </Flex>
    </AppContainer>
  );
};

const HomeLogo = () => {
  return <LogoCaf className="-mt-[2px] h-[24px] w-[48px]" />;
};

export {
  BackToButton,
  HeaderComp,
  HomeLogo,
  MenuIcon,
  MobileNavbarBody,
  MobileNavbarContent,
  MobileNavbarHeader,
  MobileNavbarRoot,
  MobileWithBackContainer,
  useMobileNavbar,
};
